import { createClient as createSupabaseClient } from "@supabase/supabase-js";
import authClient, {
  getSession as getAuthSession,
  signOut as authSignOut,
  useSession,
  signIn,
} from "../auth-client";

type SupabaseAuthResponse<T> = { data: T; error: null };

// Normalize URL: remove trailing slashes for proper REST API path construction
function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, "");
}

const SUPABASE_URL = normalizeUrl(
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
    process.env.NEXT_PUBLIC_MSTROHUB_SUPABASE_URL ||
    ""
);
const SUPABASE_ANON_KEY =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_MSTROHUB_SUPABASE_ANON_KEY ||
  "";

function toSupabaseSession(
  session: Awaited<ReturnType<typeof getAuthSession>>
) {
  if (!session) return null;
  return {
    ...session,
    access_token: session.token ?? "",
    refresh_token: session.token ?? "",
  };
}

/**
 * Backwards-compatible client shim.
 * - auth.* methods are backed by Better Auth (@/lib/auth-client)
 * - data methods (from/storage) use Supabase JS for feedback/storage flows
 */
export function createClient() {
  const dataClient =
    SUPABASE_URL && SUPABASE_ANON_KEY
      ? createSupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY)
      : null;

  return {
    auth: {
      async getSession(): Promise<SupabaseAuthResponse<{ session: any }>> {
        const session = await getAuthSession();
        return { data: { session: toSupabaseSession(session) }, error: null };
      },
      async getUser(): Promise<SupabaseAuthResponse<{ user: any }>> {
        const session = await getAuthSession();
        return { data: { user: session?.user ?? null }, error: null };
      },
      async signOut() {
        await authSignOut();
        return { error: null };
      },
      async setSession(_session: any) {
        // Better Auth handles session via cookies; nothing to set client-side
        return { data: { session: await getAuthSession() }, error: null };
      },
    },
    from: (...args: Parameters<NonNullable<typeof dataClient>["from"]>) => {
      if (!dataClient) {
        throw new Error("Supabase data client unavailable (missing env vars)");
      }
      return dataClient.from(...args);
    },
    storage: dataClient?.storage,
  };
}

export function createHiveClient() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      "Supabase client unavailable. Ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are set."
    );
  }
  return createSupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY);
}

export { authClient as default, useSession, signIn, authSignOut as signOut };
